import { portalFormat, portalLocale, portalText } from "../i18n/portal.js";
/**
 * Caller confirmation in History, Live and the test console — plan-029 unit **WAPP-HIST2** (design §40.12.2, §40.12.3,
 * §40.13.1, §40.13.2, §40.19.5; FR-CI-16, FR-CI-18; AC-20, AC-21; ADR 0082, proposed), over API-CONF's fields:
 *
 *   GET /v1/conversations/{id}             `party_confirmation { method, outcome, confirmed_at, locked_at,
 *                                           failed_attempt_count, code_sent_count } | null`
 *   conversation_event `party_confirmation` `detail { code, method, source, attempts_left, ordinal, capability,
 *                                           available_method, last_three_digit, from_ms, to_ms }` (allow-listed)
 *   the transcript stream                  `conversation.event { kind: "party_confirmation", detail }` (Live), and in a
 *                                           TEST conversation `confirmation_code_shown { code }` (the test console only)
 *
 * NEVER A CODE IN HISTORY OR LIVE. Nothing here reads a code, a passcode or a number from a conversation's record: the
 * api answers codes and counts, at most a destination's last three digits. The only code the portal ever holds is the
 * test console's own, from `confirmation_code_shown`, which the api relays only to the person who started that test;
 * {@link consoleCodeOf} accepts exactly 4 to 8 digits and nothing else, and it is never stored.
 *
 * THE FIELD IS OPTIONAL on the view because the in-memory gateway builds its own literals; the api gateway always fills
 * it (`null` when the conversation was never confirmed or locked).
 */
import { readRecord, readText, readWholeNumber, MalformedResponseError } from "./decode.js";

/** How a caller confirmed (`party_confirmation.method`). */
export type PartyConfirmationRecordMethod = "sms_code" | "passcode" | "signed_visitor";

/** The detail's `party_confirmation`: the conversation's confirmation, codes and counts only. */
export interface PartyConfirmationRecordView {
  readonly method: PartyConfirmationRecordMethod;
  readonly outcome: "confirmed" | "locked";
  readonly confirmedAt: string | null;
  readonly lockedAt: string | null;
  readonly failedAttemptCount: number;
  readonly codeSentCount: number;
}

declare module "./gateway.js" {
  interface PastConversationView {
    /**
     * plan-029: how the caller confirmed who they were, or that confirmation locked; `null` when neither happened.
     * Absent (the in-memory gateway): neither happened.
     */
    readonly partyConfirmation?: PartyConfirmationRecordView | null;
  }
}

const METHOD = ["sms_code", "passcode", "signed_visitor"] as const;

function oneOf<T extends string>(allowed: readonly T[], value: unknown, path: string): T {
  const raw = readText(value, path);
  const found = allowed.find((one) => one === raw);
  if (found === undefined) throw new MalformedResponseError(path, allowed.join(" | "), raw);
  return found;
}

/** `party_confirmation` off the conversation's detail. Required on the wire since API-CONF. */
export function partyConfirmationOfWire(data: Readonly<Record<string, unknown>>, path: string): PartyConfirmationRecordView | null {
  const value = data["party_confirmation"];
  if (value === null) return null;
  const one = readRecord(value, `${path}.party_confirmation`);
  const at = (key: string): string | null => (one[key] === null ? null : readText(one[key], `${path}.party_confirmation.${key}`));
  return {
    method: oneOf(METHOD, one["method"], `${path}.party_confirmation.method`),
    outcome: oneOf(["confirmed", "locked"] as const, one["outcome"], `${path}.party_confirmation.outcome`),
    confirmedAt: at("confirmed_at"),
    lockedAt: at("locked_at"),
    failedAttemptCount: readWholeNumber(one["failed_attempt_count"], `${path}.party_confirmation.failed_attempt_count`),
    codeSentCount: readWholeNumber(one["code_sent_count"], `${path}.party_confirmation.code_sent_count`),
  };
}

/** "text message code", "passcode", "signed-in website visit" — the method inside a sentence. */
export function confirmationMethodWord(method: string | null): string | null {
  switch (method) {
    case "sms_code":
      return portalText("partyConfirmation", "history.method.sms_code");
    case "passcode":
      return portalText("partyConfirmation", "history.method.passcode");
    case "signed_visitor":
      return portalText("partyConfirmation", "history.method.signed_visitor");
    default:
      return null;
  }
}

function timeOf(at: string): string {
  return portalFormat(portalLocale()).date(at, { hour: "2-digit", minute: "2-digit" });
}

/** "Caller confirmed by text message code at 14:02" / "Could not confirm the caller" — History's line. */
export function partyConfirmationSummaryOf(record: PartyConfirmationRecordView): string {
  if (record.outcome === "locked") return portalText("partyConfirmation", "history.locked");
  const method = confirmationMethodWord(record.method) ?? "";
  return record.confirmedAt === null
    ? portalText("partyConfirmation", "history.confirmed_untimed", { method })
    : portalText("partyConfirmation", "history.confirmed", { method, time: timeOf(record.confirmedAt) });
}

/** "Codes sent: 2 · wrong entries: 1", or `null` when no code was sent and nothing was wrong. */
export function partyConfirmationCountsOf(record: PartyConfirmationRecordView): string | null {
  if (record.codeSentCount === 0 && record.failedAttemptCount === 0) return null;
  return portalText("partyConfirmation", "history.summary_codes", { codes: record.codeSentCount, wrong: record.failedAttemptCount });
}

function capabilityWord(value: unknown): string | null {
  switch (value) {
    case "bi_query":
      return portalText("partyConfirmation", "capability.bi_query");
    case "caller_recall":
      return portalText("partyConfirmation", "capability.caller_recall");
    case "connection_operation":
      return portalText("partyConfirmation", "capability.connection_operation");
    case "every_tool":
      return portalText("partyConfirmation", "capability.every_tool");
    default:
      return null;
  }
}

function sourceWord(value: unknown): string | null {
  switch (value) {
    case "keypad":
      return portalText("partyConfirmation", "history.source.keypad");
    case "typed":
      return portalText("partyConfirmation", "history.source.typed");
    case "speech":
      return portalText("partyConfirmation", "history.source.speech");
    default:
      return null;
  }
}

function wrongOf(detail: Readonly<Record<string, unknown>>, kind: "code" | "passcode"): string {
  const left = typeof detail["attempts_left"] === "number" ? detail["attempts_left"] : null;
  const code = kind === "code";
  const sentence =
    left === null || left <= 0
      ? portalText("partyConfirmation", code ? "history.code_wrong_none" : "history.passcode_wrong_none")
      : left === 1
        ? portalText("partyConfirmation", code ? "history.code_wrong_single" : "history.passcode_wrong_single")
        : portalText("partyConfirmation", code ? "history.code_wrong" : "history.passcode_wrong", { left });
  const source = sourceWord(detail["source"]);
  return source === null ? sentence : `${sentence} · ${source}`;
}

/**
 * One `party_confirmation` event of "What happened" in plain words (design §40.12.2): "Code didn't match (2 tries
 * left) · keyed in", "Code sent by text message to the number ending …123", "Questions about your data: the caller had
 * to confirm who they are first". Codes only arrive; an unknown code reads as "Caller confirmation", never raw.
 */
export function partyConfirmationEventSentenceOf(detail: Readonly<Record<string, unknown>>): string {
  switch (detail["code"]) {
    case "code_requested":
      return portalText("partyConfirmation", "history.code_requested");
    case "code_sent": {
      const lastThree = detail["last_three_digit"];
      return typeof lastThree === "string" && /^[0-9]{3}$/.test(lastThree)
        ? portalText("partyConfirmation", "history.code_sent", { last_three: lastThree })
        : portalText("partyConfirmation", "history.code_sent_plain");
    }
    case "code_pending":
      return portalText("partyConfirmation", "history.code_pending");
    case "code_refused":
      return portalText("partyConfirmation", "history.code_refused");
    case "code_wrong":
      return wrongOf(detail, "code");
    case "code_expired":
      return portalText("partyConfirmation", "history.code_expired");
    case "confirmation_locked":
      return portalText("partyConfirmation", "history.locked");
    case "confirmed":
    case "signed_visitor_confirmed": {
      const method = confirmationMethodWord(
        typeof detail["method"] === "string" ? detail["method"] : detail["code"] === "signed_visitor_confirmed" ? "signed_visitor" : null,
      );
      const sentence =
        method === null ? portalText("partyConfirmation", "live.confirmed") : portalText("partyConfirmation", "history.confirmed_untimed", { method });
      const source = sourceWord(detail["source"]);
      return source === null ? sentence : `${sentence} · ${source}`;
    }
    case "capability_refused": {
      const capability = capabilityWord(detail["capability"]);
      return capability === null
        ? portalText("partyConfirmation", "history.event")
        : portalText("partyConfirmation", "history.capability_refused", { capability });
    }
    case "recording_blanked":
      return portalText("partyConfirmation", "history.recording_blanked");
    case "passcode_asked":
      return portalText("partyConfirmation", "history.passcode_asked");
    case "passcode_wrong":
      return wrongOf(detail, "passcode");
    case "passcode_locked":
      return portalText("partyConfirmation", "history.passcode_locked");
    default:
      return portalText("partyConfirmation", "history.event");
  }
}

/** Live's confirmation state: waiting for a code, confirmed, could not confirm — or nothing to say. */
export type LiveConfirmationState = "pending" | "confirmed" | "locked" | null;

/**
 * The state after one `party_confirmation` event (design §40.9.1): a code sent or a passcode asked waits; a
 * confirmation confirms; a lock ends every confirmation; anything else leaves the state as it was.
 */
export function liveConfirmationStateAfter(state: LiveConfirmationState, detail: Readonly<Record<string, unknown>>): LiveConfirmationState {
  switch (detail["code"]) {
    case "code_sent":
    case "code_pending":
    case "passcode_asked":
      return state === "locked" ? state : "pending";
    case "confirmed":
    case "signed_visitor_confirmed":
      return state === "locked" ? state : "confirmed";
    case "confirmation_locked":
    case "passcode_locked":
      return "locked";
    case "code_expired":
    case "code_refused":
      return state === "pending" ? null : state;
    default:
      return state;
  }
}

/** The words for Live's state, or `null`. */
export function liveConfirmationText(state: LiveConfirmationState): string | null {
  switch (state) {
    case "pending":
      return portalText("partyConfirmation", "live.pending");
    case "confirmed":
      return portalText("partyConfirmation", "live.confirmed");
    case "locked":
      return portalText("partyConfirmation", "live.locked");
    case null:
      return null;
  }
}

/** A transcript frame's `conversation.event` data, when it is a `party_confirmation` event: its detail; else `null`. */
export function partyConfirmationFrameDetailOf(data: unknown): Readonly<Record<string, unknown>> | null {
  if (typeof data !== "object" || data === null || Array.isArray(data)) return null;
  const record = data as Readonly<Record<string, unknown>>;
  if (record["kind"] !== "party_confirmation") return null;
  const detail = record["detail"];
  return typeof detail === "object" && detail !== null && !Array.isArray(detail) ? (detail as Readonly<Record<string, unknown>>) : {};
}

/** The SSE event the test console's code arrives as (design §40.19.5, ED-17). */
export const CONFIRMATION_CODE_SHOWN_EVENT = "confirmation_code_shown";

/** A `confirmation_code_shown` frame's code: 4 to 8 digits, or `null` for anything else (never shown). */
export function consoleCodeOf(data: unknown): string | null {
  if (typeof data !== "object" || data === null || Array.isArray(data)) return null;
  const code = (data as Readonly<Record<string, unknown>>)["code"];
  return typeof code === "string" && /^[0-9]{4,8}$/.test(code) ? code : null;
}

/** "482 913" — a six-digit code in two groups of three, as the text message would write it; others as they are. */
export function groupedConsoleCode(code: string): string {
  return code.length === 6 ? `${code.slice(0, 3)} ${code.slice(3)}` : code;
}
